import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { extractVariables, renderMessage } from '@shared/broadcast'
import {
  detectDelimiter,
  inferColumns,
  parseCsv,
  toRecipients,
  type ImportResult,
} from '@shared/csv'
import { formatBytes, MAX_SIZE } from '@shared/media'
import { AUTO_CHECK_MAX_UNKNOWN } from '@shared/reachability'
import { api, ApiError, type DuplicateBroadcastSource, type ProductView, type UploadedMedia } from '../lib/api'
import { openRazorpayOrderCheckout } from '../lib/razorpayCheckout'
import { describeRecurrence, EMPTY_RECURRENCE, RecurrenceFields, recurrencePayload, type RecurrenceState } from './RecurrenceFields'
import { ReadOnlyNotice } from './ReadOnlyNotice'

/** UX-only floor to steer the quantity picker; the server is the actual source of truth. */
const TOPUP_MIN_CREDITS = 100

const STATUS_TONE: Record<string, 'good' | 'warn' | 'bad' | undefined> = {
  sent: 'good',
  sending: 'warn',
  scheduled: 'warn',
  failed: 'bad',
  cancelled: undefined,
  draft: undefined,
}

/**
 * Bulk messaging.
 *
 * The composer leads with what the send will cost and how long it will take, because both
 * answers are frequently unwelcome — a young number may only carry twenty conversations
 * today — and finding that out afterwards is how numbers get banned and campaigns
 * half-finish.
 */
export function Broadcasts() {
  const queryClient = useQueryClient()
  const [composing, setComposing] = useState(false)
  const [duplicateSource, setDuplicateSource] = useState<DuplicateBroadcastSource | null>(null)
  // `DuplicateBroadcastSource` is a snapshot payload with no id of its own — this is what the
  // composer keys on to tell one duplicated draft from the next.
  const [duplicateOfId, setDuplicateOfId] = useState<string | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)

  const data = useQuery({ queryKey: ['broadcasts'], queryFn: api.broadcasts, retry: false })
  // Why an agent's send is not moving, said next to the send rather than left to be puzzled over.
  const agentSend = useQuery({
    queryKey: ['agent-send-settings'],
    queryFn: api.agentSendSettings,
    retry: false,
    enabled: (data.data?.broadcasts ?? []).some((run) => run.createdByAgentId),
  })
  const agentHold = agentSend.data
    ? agentSend.data.settings.paused
      ? 'agent sending is paused'
      : !agentSend.data.open
        ? 'waiting for agent send hours'
        : null
    : null
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['broadcasts'] })

  const detail = useQuery({
    queryKey: ['broadcast', detailId],
    queryFn: () => api.broadcast(detailId!),
    enabled: Boolean(detailId),
    retry: false,
  })

  const cancel = useMutation({ mutationFn: api.cancelBroadcast, onSuccess: refresh })
  const retryFailed = useMutation({ mutationFn: api.retryFailedBroadcast, onSuccess: refresh })
  const duplicate = useMutation({
    mutationFn: api.duplicateBroadcastSource,
    onSuccess: (source) => {
      setDuplicateSource(source)
      setComposing(true)
    },
  })

  if (data.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (data.isError) {
    const message = data.error instanceof ApiError ? data.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { broadcasts, numbers, credits, canSend, savedLists, templates } = data.data!

  return (
    <>
      {/* `canSend`, not `canManage` — same read-only situation, different flag name. */}
      {!canSend && <ReadOnlyNotice what="bulk sends" />}
      <header className="stack gap-1">
        <span className="cad-eyebrow">Bulk messaging</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">Sends</h1>
          <div className="row gap-2 wrap">
            <span className="cad-pill" data-tone={credits.monthlyRemaining > 0 ? undefined : 'bad'}>
              {credits.monthlyRemaining.toLocaleString()} credits left
            </span>
            {credits.topupRemaining > 0 && (
              <span className="cad-pill">{credits.topupRemaining.toLocaleString()} top-up</span>
            )}
            <BuyCreditsButton onPurchased={refresh} />
            {canSend && !composing && (
              <button type="button" className="cad-btn" onClick={() => setComposing(true)}>
                New send
              </button>
            )}
          </div>
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Messages are spaced out over hours rather than sent at once. WhatsApp scores every
          number on behaviour, and a burst to people who have not messaged you first is the
          clearest signal there is of a bot.
        </p>
      </header>

      {composing && (
        <Composer
          // "Duplicate" on one broadcast, then another, before closing the composer left the
          // form's fields (name, body, recipients, media) showing whatever was seeded first —
          // `prefill` changed but the same instance's `useState` initializers never re-ran.
          key={duplicateOfId ?? 'new'}
          numbers={numbers}
          savedLists={savedLists}
          templates={templates}
          prefill={duplicateSource}
          onDone={() => {
            setComposing(false)
            setDuplicateSource(null)
            setDuplicateOfId(null)
            void refresh()
          }}
          onCancel={() => {
            setComposing(false)
            setDuplicateSource(null)
            setDuplicateOfId(null)
          }}
        />
      )}

      {broadcasts.length === 0 ? (
        <div className="cad-card">
          <p className="cad-muted">
            Nothing sent yet. A broadcast goes to a saved contact segment from one of your numbers,
            paced so it does not look like a blast.{' '}
            {canSend
              ? 'Build the segment on Contacts first, then start a send from here.'
              : 'Your role cannot start one.'}
          </p>
        </div>
      ) : (
        <section className="cad-card">
          <div className="stack gap-3">
            <h2 className="cad-section-title">History</h2>
            {(cancel.error || retryFailed.error || duplicate.error) && (
              <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                {(() => {
                  const err = cancel.error ?? retryFailed.error ?? duplicate.error
                  return err instanceof ApiError || err instanceof Error ? err.message : 'That did not go through.'
                })()}
              </p>
            )}
            <div className="cad-grid-frame">
              <table className="cad-table">
                <thead>
                  <tr>
                    <th>Send</th>
                    <th>Status</th>
                    <th className="num">Sent</th>
                    <th className="num">Pending</th>
                    <th className="num">Deferred</th>
                    <th className="num">Failed</th>
                    <th />
                    {canSend && <th />}
                  </tr>
                </thead>
                <tbody>
                  {broadcasts.map((run) => (
                    <tr key={run.id}>
                      <td>
                        <div className="stack gap-1">
                          <span className="row gap-2 wrap items-baseline">
                            <span style={{ fontWeight: 500 }}>{run.name}</span>
                            {run.createdByAgentId && (
                              <span className="cad-pill" data-tone="accent" title="Started by an AI agent, not a teammate">
                                Agent{run.touchRef ? ` · ${run.touchRef}` : ''}
                              </span>
                            )}
                          </span>
                          <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                            {run.scheduledFor
                              ? `Scheduled ${new Date(run.scheduledFor).toLocaleString()}`
                              : new Date(run.createdAt).toLocaleString()}
                            {run.createdByAgentId && agentHold && (run.status === 'sending' || run.status === 'scheduled')
                              ? ` · ${agentHold}`
                              : ''}
                            {run.recurrence
                              ? ` · repeats ${describeRecurrence(
                                  run.recurrence as 'daily' | 'weekly' | 'monthly',
                                  run.recurrenceInterval,
                                  run.recurrenceEndAt,
                                  run.recurrenceEndCount,
                                  run.recurrenceWeekdays,
                                )}`
                              : ''}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="cad-pill" data-tone={STATUS_TONE[run.status]}>
                          {run.status}
                        </span>
                      </td>
                      <td className="num">{run.counts.sent ?? 0}</td>
                      <td className="num">{run.counts.pending ?? 0}</td>
                      <td className="num cad-muted">{run.counts.deferred ?? 0}</td>
                      <td className="num">{run.counts.failed ?? 0}</td>
                      <td className="num">
                        <button
                          type="button"
                          className="cad-btn"
                          data-variant="text"
                          onClick={() => setDetailId(detailId === run.id ? null : run.id)}
                        >
                          {detailId === run.id ? 'Hide' : 'Details'}
                        </button>
                      </td>
                      {canSend && (
                        <td className="num">
                          <div className="row gap-2 wrap" style={{ justifyContent: 'flex-end' }}>
                            {(run.status === 'sending' || run.status === 'scheduled') && (
                              <button
                                type="button"
                                className="cad-btn"
                                data-variant="text"
                                disabled={cancel.isPending}
                                onClick={() => {
                                  if (confirm(`Stop this broadcast?

Messages already sent cannot be recalled. The rest of the queue is dropped and the run cannot be resumed.`)) cancel.mutate(run.id)
                                }}
                              >
                                Stop
                              </button>
                            )}
                            {(run.counts.failed ?? 0) > 0 && run.status !== 'sending' && run.status !== 'scheduled' && (
                              <button
                                type="button"
                                className="cad-btn"
                                data-variant="text"
                                disabled={retryFailed.isPending}
                                onClick={() => retryFailed.mutate(run.id)}
                              >
                                Retry failed
                              </button>
                            )}
                            <button
                              type="button"
                              className="cad-btn"
                              data-variant="text"
                              disabled={duplicate.isPending}
                              onClick={() => {
                                setDuplicateOfId(run.id)
                                duplicate.mutate(run.id)
                              }}
                            >
                              Duplicate
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {detailId && (
                    <tr>
                      <td colSpan={canSend ? 8 : 7}>
                        {detail.isPending ? (
                          <p className="cad-muted">Loading recipients…</p>
                        ) : detail.isError ? (
                          <p className="cad-muted">Could not load recipients for this send.</p>
                        ) : (
                          <div className="stack gap-2" style={{ padding: '8px 0' }}>
                            <span className="cad-eyebrow">
                              {detail.data!.recipients.length.toLocaleString()} recipient
                              {detail.data!.recipients.length === 1 ? '' : 's'}
                            </span>
                            <div className="cad-grid-frame" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                              <table className="cad-table">
                                <thead>
                                  <tr>
                                    <th>Recipient</th>
                                    <th>Number</th>
                                    <th>Status</th>
                                    <th>Detail</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detail.data!.recipients.map((r) => (
                                    <tr key={r.id}>
                                      <td>{r.displayName ?? r.remoteId}</td>
                                      <td className="cad-muted">{r.channelNumber ?? '—'}</td>
                                      <td>
                                        <span className="cad-pill" data-tone={STATUS_TONE[r.status]}>
                                          {r.status}
                                        </span>
                                      </td>
                                      <td className="cad-muted">{r.error ?? '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="cad-note">
              Deferred recipients were beyond what the sending number can safely reach today.
              They are kept, not dropped, and go out on the next run.
            </p>
          </div>
        </section>
      )}
    </>
  )
}

/**
 * Purchases extra credits outside the monthly allowance, spent only once it runs out.
 * A small popover rather than a page: the moment this matters is mid-send, right here.
 */
function BuyCreditsButton({ onPurchased }: { onPurchased: () => void }) {
  const [open, setOpen] = useState(false)
  const [credits, setCredits] = useState(500)
  const [status, setStatus] = useState<'idle' | 'cancelled' | 'bought'>('idle')

  const buy = useMutation({
    mutationFn: async () => {
      setStatus('idle')
      const order = await api.topupOrder(credits)

      if (order.provider === 'stripe') {
        // Stripe takes over the page; nothing after this runs on success.
        window.location.href = order.checkoutUrl
        return 'redirecting' as const
      }

      const result = await openRazorpayOrderCheckout({
        keyId: order.keyId,
        orderId: order.orderId,
        amountMinor: order.amountMinor,
        currency: order.currency,
        name: order.name,
        description: order.description,
        prefill: order.prefill,
      })
      if (!result) return 'cancelled' as const

      await api.topupConfirm({
        orderId: result.razorpay_order_id,
        paymentId: result.razorpay_payment_id,
        signature: result.razorpay_signature,
      })
      return 'bought' as const
    },
    onSuccess: (outcome) => {
      if (outcome === 'cancelled') setStatus('cancelled')
      if (outcome === 'bought') {
        setStatus('bought')
        onPurchased()
      }
    },
  })

  const errorMessage =
    buy.error instanceof ApiError || buy.error instanceof Error ? buy.error.message : undefined

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="cad-btn" data-variant="text" onClick={() => setOpen((v) => !v)}>
        Buy credits
      </button>
      {open && (
        <div
          className="cad-card stack gap-3"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 10,
            minWidth: '260px',
            marginTop: '0.5rem',
          }}
        >
          <div className="row between gap-2" style={{ alignItems: 'center' }}>
            <strong>Buy credits</strong>
            <button type="button" className="cad-btn" data-variant="quiet" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
          <label className="stack gap-1">
            <span className="cad-muted">Credits (minimum {TOPUP_MIN_CREDITS.toLocaleString()})</span>
            <input
              type="number"
              min={TOPUP_MIN_CREDITS}
              step={100}
              value={credits}
              onChange={(e) => setCredits(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            />
          </label>
          <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
            Top-up credits never expire and are spent only after your monthly allowance runs out.
            Price and currency are shown at checkout.
          </p>
          {status === 'cancelled' && <p className="cad-note">Checkout closed. Nothing was charged.</p>}
          {status === 'bought' && <p className="cad-note">Credits added. Thank you.</p>}
          {errorMessage && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {errorMessage}
            </p>
          )}
          <button
            type="button"
            className="cad-btn"
            disabled={buy.isPending || credits < TOPUP_MIN_CREDITS}
            onClick={() => buy.mutate()}
          >
            {buy.isPending ? 'Opening…' : `Buy ${credits.toLocaleString()} credits`}
          </button>
        </div>
      )}
    </div>
  )
}

/** Dial codes offered for lists that hold national numbers with no country code. */
const DIAL_CODES = [
  { code: '', label: 'Numbers already include the country code' },
  { code: '91', label: 'India +91' },
  { code: '1', label: 'United States / Canada +1' },
  { code: '44', label: 'United Kingdom +44' },
  { code: '971', label: 'United Arab Emirates +971' },
  { code: '65', label: 'Singapore +65' },
  { code: '61', label: 'Australia +61' },
  { code: '55', label: 'Brazil +55' },
  { code: '27', label: 'South Africa +27' },
  { code: '234', label: 'Nigeria +234' },
  { code: '62', label: 'Indonesia +62' },
]

/** A duplicated send's recipients re-enter through the same CSV pipeline every pasted or
 *  imported list already goes through, rather than a second "set recipients directly" state
 *  path — one recipient-parsing route to keep correct, not two. */
function sourceToCsv(source: DuplicateBroadcastSource): string {
  const variableNames = source.variables.map((v) => v.name)
  const header = ['phone', 'name', ...variableNames]
  const rows = source.recipients.map((r) => [
    r.remoteId,
    r.displayName ?? '',
    ...variableNames.map((v) => r.variables[v] ?? ''),
  ])
  return [header, ...rows].map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n')
}

function Composer({
  numbers,
  savedLists,
  templates,
  prefill,
  onDone,
  onCancel,
}: {
  numbers: { id: string; phone: string; label: string; maturity: string }[]
  savedLists: { id: string; name: string; entries: { remoteId: string; displayName?: string }[] }[]
  templates: { id: string; name: string; body: string; media: UploadedMedia | null }[]
  prefill?: DuplicateBroadcastSource | null
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(prefill ? `Copy of ${prefill.name}` : '')
  const [body, setBody] = useState(prefill?.body ?? '')
  const [audienceMode, setAudienceMode] = useState<'paste' | 'segment'>('paste')
  const [segmentId, setSegmentId] = useState('')
  const [rawRecipients, setRawRecipients] = useState(() => (prefill ? sourceToCsv(prefill) : ''))
  const [sourceName, setSourceName] = useState<string | null>(prefill ? `Duplicated from "${prefill.name}"` : null)
  const [scheduledFor, setScheduledFor] = useState('')
  const [recurrenceState, setRecurrenceState] = useState<RecurrenceState>(EMPTY_RECURRENCE)
  const [dialCode, setDialCode] = useState('')
  const [media, setMedia] = useState<UploadedMedia | null>(prefill?.media ?? null)
  const [mediaPreview, setMediaPreview] = useState<string | null>(null)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [savingList, setSavingList] = useState(false)
  const [listName, setListName] = useState('')
  const [showRejected, setShowRejected] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  // Every connected number is selected by default — the same "spread across everything
  // connected" behaviour a send always had before it could be scoped to specific numbers.
  const [selectedNumberIds, setSelectedNumberIds] = useState<Set<string>>(() => new Set(numbers.map((n) => n.id)))
  const toggleNumber = (id: string) =>
    setSelectedNumberIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  // Sent as undefined, not an explicit list, when everything is selected — the server's own
  // "empty or missing means every connected number" default, so a newly-connected number is
  // included automatically rather than needing every saved send to be re-edited to see it.
  const numberIds = selectedNumberIds.size === numbers.length ? undefined : [...selectedNumberIds]
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const fileId = useId()
  const mediaId = useId()

  /**
   * Pasted text and an uploaded file go through exactly the same path.
   *
   * A pasted block is a CSV whether or not it came from a file, and treating them differently
   * is how one of the two ends up with the careful handling and the other with a naive split
   * on commas.
   */
  const rows = useMemo(() => {
    if (!rawRecipients.trim()) return []
    return parseCsv(rawRecipients, detectDelimiter(rawRecipients))
  }, [rawRecipients])

  const guess = useMemo(() => inferColumns(rows), [rows])

  // Overrides are cleared whenever a new list is loaded, so the inference is re-applied
  // rather than a previous file's column choice silently carrying over to the next.
  const [mapping, setMapping] = useState<{ phone: number; name: number; header: boolean } | null>(null)
  useEffect(() => {
    setMapping(null)
    setShowRejected(false)
  }, [rawRecipients])

  const phoneIndex = mapping?.phone ?? guess.phoneIndex
  const nameIndex = mapping?.name ?? guess.nameIndex
  const hasHeader = mapping?.header ?? guess.hasHeader
  const setMap = (patch: Partial<{ phone: number; name: number; header: boolean }>) =>
    setMapping({ phone: phoneIndex, name: nameIndex, header: hasHeader, ...patch })

  const imported = useMemo(() => {
    if (rows.length === 0 || phoneIndex < 0) {
      return { recipients: [], rejected: [], duplicates: 0, variables: [] } as ImportResult
    }
    return toRecipients(rows, {
      phoneIndex,
      nameIndex,
      hasHeader,
      defaultDialCode: dialCode || undefined,
    })
  }, [rows, phoneIndex, nameIndex, hasHeader, dialCode])

  const recipients = imported.recipients
  const columnCount = rows.length > 0 ? Math.max(...rows.map((r) => r.length)) : 0

  const variableNames = useMemo(() => extractVariables(body), [body])
  const variables = variableNames.map((v) => ({ name: v, fallback: v === 'name' ? 'there' : '' }))

  const upload = useMutation({
    mutationFn: api.uploadMedia,
    onSuccess: (result) => setMedia(result),
  })

  const onFile = async (file: File | undefined) => {
    if (!file) return
    setSourceName(file.name)
    setRawRecipients(await file.text())
  }

  const onAttach = (file: File | undefined) => {
    if (!file) return
    upload.reset()
    // Previewed from the local file rather than from storage: the bytes are already here, and
    // a round trip would only add a spinner.
    setMediaPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : null)
    upload.mutate(file)
  }

  const insertVariable = (variable: string) => {
    const field = bodyRef.current
    const token = `{${variable}}`
    if (!field) {
      setBody((current) => current + token)
      return
    }
    const start = field.selectionStart ?? body.length
    const end = field.selectionEnd ?? body.length
    setBody(body.slice(0, start) + token + body.slice(end))
    requestAnimationFrame(() => {
      field.focus()
      field.setSelectionRange(start + token.length, start + token.length)
    })
  }

  const contactSegments = useQuery({ queryKey: ['segments'], queryFn: api.segments, retry: false })
  const audienceSegments = (contactSegments.data?.segments ?? []).filter((s) => s.entity === 'contact')

  const audienceReady = audienceMode === 'segment' ? Boolean(segmentId) : recipients.length > 0

  const preview = useQuery({
    queryKey: ['broadcast-preview', body, audienceMode, segmentId, recipients.length, dialCode, phoneIndex, numberIds],
    queryFn: () =>
      audienceMode === 'segment'
        ? api.previewBroadcast({ body, variables, segmentId, numberIds })
        : api.previewBroadcast({ body, variables, recipients, numberIds }),
    enabled: audienceReady && (body.trim().length > 0 || media !== null),
    retry: false,
  })

  const composerQueryClient = useQueryClient()

  const saveTemplate = useMutation({
    mutationFn: () => api.saveBroadcastTemplate({ name: templateName.trim(), body, media }),
    onSuccess: () => {
      setSavingTemplate(false)
      setTemplateName('')
      void composerQueryClient.invalidateQueries({ queryKey: ['broadcasts'] })
    },
  })

  const deleteTemplate = useMutation({
    mutationFn: (id: string) => api.deleteBroadcastTemplate(id),
    onSuccess: () => void composerQueryClient.invalidateQueries({ queryKey: ['broadcasts'] }),
  })

  // Fetched lazily by the composer itself rather than threaded down as a prop from Broadcasts()
  // — a catalogue is optional, most sends never touch it, and every recipient of one broadcast
  // shares the same product/price, so one payment link covers the whole send rather than one
  // per recipient.
  const products = useQuery({ queryKey: ['products'], queryFn: api.products })
  const useProduct = useMutation({
    mutationFn: (product: ProductView) => api.createProductPaymentLink(product.id).then((link) => ({ product, link })),
    onSuccess: ({ product, link }) => {
      const price = `${(product.priceMinor / 100).toFixed(2)} ${product.currency}`
      setBody([product.name, product.description, price, link.url].filter(Boolean).join('\n'))
      if (product.mediaKey) {
        setMedia({
          key: product.mediaKey,
          kind: product.mediaKind ?? 'image',
          mime: product.mediaMime ?? 'image/jpeg',
          fileName: product.mediaFileName ?? 'image',
          size: product.mediaSize ?? 0,
        })
        setMediaPreview(null)
      }
    },
  })

  const saveList = useMutation({
    mutationFn: () =>
      api.saveList({
        name: listName.trim(),
        entries: recipients.map((r) => ({ remoteId: r.remoteId, displayName: r.displayName })),
      }),
    onSuccess: () => {
      setSavingList(false)
      setListName('')
      void composerQueryClient.invalidateQueries({ queryKey: ['broadcasts'] })
    },
  })

  /**
   * Asks WhatsApp which of this audience actually has an account.
   *
   * Never something the preview does for itself: the preview refires as the customer types and
   * this one reaches the WhatsApp socket. It runs from the button, or automatically — but keyed
   * to the audience, not the message (see the effect below), so typing never triggers it.
   * Re-running the preview afterwards is what turns the fresh answers into the reachable line,
   * and it matters that the line comes back through the preview rather than from this response
   * — the preview reads the same cache the send will, so the figure shown is the figure charged.
   */
  const reachability = useMutation({
    mutationFn: (_options?: { auto: boolean }) =>
      audienceMode === 'segment'
        ? api.checkReachability({ segmentId, numberIds })
        : api.checkReachability({
            recipients: recipients.map((r) => ({ remoteId: r.remoteId })),
            numberIds,
          }),
    onSuccess: () => composerQueryClient.invalidateQueries({ queryKey: ['broadcast-preview'] }),
  })

  const create = useMutation({
    mutationFn: () =>
      api.createBroadcast({
        name: name || sourceName || 'Untitled send',
        body,
        variables,
        ...(audienceMode === 'segment' ? { segmentId } : { recipients }),
        scheduledFor: scheduledFor ? new Date(scheduledFor).toISOString() : null,
        ...recurrencePayload(recurrenceState),
        media,
        numberIds,
      }),
    onSuccess: onDone,
  })

  const error = create.error
  const message = error instanceof ApiError || error instanceof Error ? error.message : undefined
  const p = preview.data

  // A failed automatic check stays quiet — "connect a number" on every audience change would be
  // noise, and the send is unaffected: an unchecked recipient is always kept, only a confirmed
  // unreachable one is dropped. The button is still there, and its failures are still shown.
  const lastCheckWasAuto = reachability.variables?.auto === true
  const reachabilityError =
    !lastCheckWasAuto && (reachability.error instanceof ApiError || reachability.error instanceof Error)
      ? reachability.error.message
      : undefined

  /**
   * The automatic check. Keyed to the audience — the segment, or the exact list of numbers — and
   * deliberately not to the message text: the preview refires on every keystroke and this
   * reaches the WhatsApp socket. Once per audience, after it has sat unchanged long enough for
   * the preview to load (so a customer flicking through segments checks none of them), and only
   * for audiences small enough to read as ordinary traffic — see `AUTO_CHECK_MAX_UNKNOWN`.
   * A failure is not retried; the button remains for that.
   */
  const audienceKey = useMemo(
    () =>
      audienceMode === 'segment'
        ? `segment:${segmentId}:${numberIds?.join(',') ?? ''}`
        : `list:${recipients.map((r) => r.remoteId).join(',')}:${numberIds?.join(',') ?? ''}`,
    [audienceMode, segmentId, recipients, numberIds],
  )
  const autoCheckedKey = useRef<string | null>(null)
  const unknownCount = p?.reachability.unknown ?? 0
  const { mutate: runReachability, isPending: checkingReachability } = reachability
  useEffect(() => {
    if (!audienceReady || checkingReachability) return
    if (unknownCount === 0 || unknownCount > AUTO_CHECK_MAX_UNKNOWN) return
    if (autoCheckedKey.current === audienceKey) return
    const timer = setTimeout(() => {
      autoCheckedKey.current = audienceKey
      runReachability({ auto: true })
    }, 1500)
    return () => clearTimeout(timer)
  }, [audienceKey, audienceReady, unknownCount, checkingReachability, runReachability])

  return (
    <section className="cad-card">
      <div className="stack gap-4">
        <h2 className="cad-section-title">New send</h2>

        <div className="field-grid">
          <div className="cad-field">
            <label htmlFor="bc-name">Name this send</label>
            <input
              id="bc-name"
              className="cad-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="February restock"
            />
          </div>
          <div className="cad-field">
            <label htmlFor="bc-when">Send at (leave blank for now)</label>
            <input
              id="bc-when"
              className="cad-input"
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
            />
          </div>
          <div className="cad-field">
            <label htmlFor="bc-repeat">Repeat</label>
            <select
              id="bc-repeat"
              className="cad-input"
              value={recurrenceState.recurrence}
              onChange={(e) =>
                setRecurrenceState({ ...recurrenceState, recurrence: e.target.value as RecurrenceState['recurrence'] })
              }
            >
              <option value="">Once</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>

        <RecurrenceFields state={recurrenceState} onChange={setRecurrenceState} idPrefix="bc" />

        {numbers.length > 1 && (
          <div className="cad-field">
            <label>Send from</label>
            <div className="row gap-2 wrap">
              {numbers.map((n) => (
                <label
                  key={n.id}
                  className="cad-pill"
                  data-tone={selectedNumberIds.has(n.id) ? 'accent' : undefined}
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={selectedNumberIds.has(n.id)}
                    onChange={() => toggleNumber(n.id)}
                    style={{ marginRight: '6px' }}
                  />
                  {n.label} · {n.phone}
                </label>
              ))}
            </div>
            {selectedNumberIds.size === 0 && (
              <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                Choose at least one number, or this send has nowhere to go from.
              </p>
            )}
            <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              Recipients are spread across whichever of these are chosen, the same pacing as
              always — just narrowed to this set instead of every connected number.
            </p>
          </div>
        )}

        {/* ---------------------------------------------------------------- recipients */}

        <div className="cad-field">
          <label>Audience</label>
          <div className="row gap-2 wrap">
            <button
              type="button"
              className="cad-pill"
              data-tone={audienceMode === 'paste' ? 'accent' : undefined}
              onClick={() => setAudienceMode('paste')}
            >
              Paste or import
            </button>
            <button
              type="button"
              className="cad-pill"
              data-tone={audienceMode === 'segment' ? 'accent' : undefined}
              onClick={() => setAudienceMode('segment')}
            >
              Use a segment
            </button>
          </div>
        </div>

        {audienceMode === 'segment' ? (
          <div className="cad-field">
            <label htmlFor="bc-segment">Segment</label>
            <select
              id="bc-segment"
              className="cad-input"
              value={segmentId}
              onChange={(e) => setSegmentId(e.target.value)}
            >
              <option value="">Choose a segment…</option>
              {audienceSegments.map((segment) => (
                <option key={segment.id} value={segment.id}>
                  {segment.name}
                </option>
              ))}
            </select>
            {audienceSegments.length === 0 && (
              <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                No contact segments yet — build one from the Contacts screen.
              </p>
            )}
            {segmentId && (
              <p className="cad-note">
                {audienceSegments.find((s) => s.id === segmentId)?.description}. Resolved against
                current contacts at send time, so someone who opts out beforehand is never
                messaged — even if they matched when this send was scheduled.
              </p>
            )}
          </div>
        ) : (
        <>
        <div className="cad-field">
          <div className="row between wrap gap-2" style={{ alignItems: 'baseline' }}>
            <label htmlFor="bc-to">Recipients</label>
            <div className="row gap-2">
              <input
                id={fileId}
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/plain"
                style={{ display: 'none' }}
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
              <label htmlFor={fileId} className="cad-btn" data-variant="quiet" role="button">
                Import a CSV
              </label>
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                onClick={() => downloadRecipientTemplate(variableNames)}
              >
                Download template
              </button>
            </div>
          </div>
          <textarea
            id="bc-to"
            className="cad-input"
            rows={5}
            value={rawRecipients}
            onChange={(e) => {
              setSourceName(null)
              setRawRecipients(e.target.value)
            }}
            placeholder={'Paste rows, or import a file.\n\nphone,name,order\n+14155559001,Priya,A-118'}
          />
          {sourceName && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              Loaded from {sourceName} · {rows.length.toLocaleString()} rows
            </span>
          )}
          {savedLists.length > 0 && (
            <div className="row gap-2 wrap" style={{ marginTop: '6px' }}>
              {savedLists.map((list) => (
                <button
                  key={list.id}
                  type="button"
                  className="cad-pill"
                  onClick={() => {
                    setSourceName(list.name)
                    setRawRecipients(
                      list.entries
                        .map((e) => `${e.remoteId}${e.displayName ? `,${e.displayName}` : ''}`)
                        .join('\n'),
                    )
                  }}
                >
                  {list.name} · {list.entries.length}
                </button>
              ))}
            </div>
          )}
          {recipients.length > 0 && (
            <div className="row gap-2 wrap" style={{ marginTop: '6px', alignItems: 'center' }}>
              {savingList ? (
                <>
                  <input
                    className="cad-input"
                    style={{ width: '180px' }}
                    autoFocus
                    value={listName}
                    onChange={(e) => setListName(e.target.value)}
                    placeholder="List name"
                  />
                  <button
                    type="button"
                    className="cad-btn"
                    data-variant="text"
                    disabled={!listName.trim() || saveList.isPending}
                    onClick={() => saveList.mutate()}
                  >
                    {saveList.isPending ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="cad-btn"
                    data-variant="quiet"
                    onClick={() => {
                      setSavingList(false)
                      setListName('')
                      saveList.reset()
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button type="button" className="cad-btn" data-variant="quiet" onClick={() => setSavingList(true)}>
                  Save this list of {recipients.length.toLocaleString()} as a saved list
                </button>
              )}
              {saveList.isError && (
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)', color: 'var(--cad-bad)' }}>
                  {saveList.error instanceof ApiError || saveList.error instanceof Error
                    ? saveList.error.message
                    : 'Could not save that.'}
                </span>
              )}
            </div>
          )}
        </div>

        {columnCount > 0 && (
          <div className="stack gap-3">
            <div className="field-grid">
              <div className="cad-field">
                <label htmlFor="bc-phone-col">Which column is the number</label>
                <select
                  id="bc-phone-col"
                  className="cad-input"
                  value={phoneIndex}
                  onChange={(e) => setMap({ phone: Number(e.target.value) })}
                >
                  <option value={-1}>Not set</option>
                  {Array.from({ length: columnCount }, (_, i) => (
                    <option key={i} value={i}>
                      {columnLabel(rows, i, hasHeader)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="cad-field">
                <label htmlFor="bc-name-col">Which column is the name</label>
                <select
                  id="bc-name-col"
                  className="cad-input"
                  value={nameIndex}
                  onChange={(e) => setMap({ name: Number(e.target.value) })}
                >
                  <option value={-1}>None</option>
                  {Array.from({ length: columnCount }, (_, i) => (
                    <option key={i} value={i}>
                      {columnLabel(rows, i, hasHeader)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="cad-field">
                <label htmlFor="bc-country">If a number has no country code</label>
                <select
                  id="bc-country"
                  className="cad-input"
                  value={dialCode}
                  onChange={(e) => setDialCode(e.target.value)}
                >
                  {DIAL_CODES.map((entry) => (
                    <option key={entry.code} value={entry.code}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasHeader}
                onChange={(e) => setMap({ header: e.target.checked })}
              />
              <span className="cad-muted">The first row is a header, not a contact</span>
            </label>

            <div className="row gap-2 wrap">
              <span className="cad-pill" data-tone={recipients.length > 0 ? 'good' : 'bad'}>
                {recipients.length.toLocaleString()} ready
              </span>
              {imported.duplicates > 0 && (
                <span className="cad-pill">
                  {imported.duplicates} duplicate{imported.duplicates === 1 ? '' : 's'} removed
                </span>
              )}
              {imported.rejected.length > 0 && (
                <button
                  type="button"
                  className="cad-pill"
                  data-tone="bad"
                  onClick={() => setShowRejected((v) => !v)}
                >
                  {imported.rejected.length} could not be read
                </button>
              )}
            </div>

            {phoneIndex < 0 && (
              <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                No column looks like a phone number. Pick the right one above.
              </p>
            )}

            {/* Shown rather than summarised: the customer needs to see which of their own
                contacts will not be reached, and which line to go and fix. */}
            {showRejected && imported.rejected.length > 0 && (
              <div className="cad-grid-frame">
                <table className="cad-table">
                  <thead>
                    <tr>
                      <th className="num">Row</th>
                      <th>Value</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imported.rejected.slice(0, 25).map((row) => (
                      <tr key={`${row.line}-${row.value}`}>
                        <td className="num">{row.line}</td>
                        <td>{row.value || <span className="cad-muted">empty</span>}</td>
                        <td className="cad-muted">{row.issue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {imported.rejected.length > 25 && (
                  <p className="cad-note">
                    and {imported.rejected.length - 25} more.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        </>
        )}

        {/* ---------------------------------------------------------------- message */}

        <div className="cad-field">
          <label htmlFor="bc-body">
            Message — use <code>{'{name}'}</code> to personalise
          </label>
          <textarea
            id="bc-body"
            ref={bodyRef}
            className="cad-input"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Hi {name}, our new stock lands Friday. Reply STOP to opt out."
          />
          {imported.variables.length > 0 && (
            <div className="row gap-2 wrap" style={{ marginTop: '6px', alignItems: 'center' }}>
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                From your file:
              </span>
              {imported.variables.map((variable) => (
                <button
                  key={variable}
                  type="button"
                  className="cad-pill"
                  onClick={() => insertVariable(variable)}
                >
                  {`{${variable}}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ---------------------------------------------------------------- attachment */}

        <div className="cad-field">
          <label htmlFor={mediaId}>Attachment</label>
          <input
            id={mediaId}
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => onAttach(e.target.files?.[0])}
          />

          {media ? (
            <div className="row gap-3" style={{ alignItems: 'center' }}>
              {mediaPreview ? (
                <img
                  src={mediaPreview}
                  alt=""
                  style={{
                    width: '64px',
                    height: '64px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
              ) : (
                <span className="cad-pill">{media.kind}</span>
              )}
              <div className="stack gap-1">
                <span style={{ fontWeight: 500 }}>{media.fileName}</span>
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                  {media.kind} · {formatBytes(media.size)}
                </span>
              </div>
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                onClick={() => {
                  setMedia(null)
                  setMediaPreview(null)
                  upload.reset()
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
              <label htmlFor={mediaId} className="cad-btn" data-variant="quiet" role="button">
                {upload.isPending ? 'Uploading…' : 'Add a photo or file'}
              </label>
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                Images up to {formatBytes(MAX_SIZE.image)}, video and documents up to{' '}
                {formatBytes(MAX_SIZE.video)}.
              </span>
            </div>
          )}

          {upload.error && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {upload.error instanceof Error ? upload.error.message : 'The file could not be uploaded.'}
            </p>
          )}
          {media && media.kind !== 'audio' && !body.trim() && (
            <p className="cad-note">
              This will send as {media.kind === 'document' ? 'a file' : media.kind} with no caption.
            </p>
          )}
        </div>

        {/* ---------------------------------------------------------------- message templates */}

        <div className="cad-field">
          <label>Message templates</label>
          <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
            {templates.map((t) => (
              <span key={t.id} className="cad-pill" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <button
                  type="button"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit' }}
                  onClick={() => {
                    setBody(t.body)
                    setMedia(t.media)
                    setMediaPreview(null)
                  }}
                >
                  {t.name}
                </button>
                <button
                  type="button"
                  aria-label={`Delete template ${t.name}`}
                  disabled={deleteTemplate.isPending}
                  onClick={() => {
                    if (confirm(`Delete the "${t.name}" template?

Broadcasts already sent are unaffected, but you will have to rewrite it to use it again.`)) deleteTemplate.mutate(t.id)
                  }}
                  style={{ marginLeft: '6px', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                >
                  ×
                </button>
              </span>
            ))}
            {savingTemplate ? (
              <>
                <input
                  className="cad-input"
                  style={{ width: '180px' }}
                  autoFocus
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Template name"
                />
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  disabled={!templateName.trim() || (!body.trim() && !media) || saveTemplate.isPending}
                  onClick={() => saveTemplate.mutate()}
                >
                  {saveTemplate.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="quiet"
                  onClick={() => {
                    setSavingTemplate(false)
                    setTemplateName('')
                    saveTemplate.reset()
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={!body.trim() && !media}
                onClick={() => setSavingTemplate(true)}
              >
                Save as template
              </button>
            )}
          </div>
          {saveTemplate.error && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {saveTemplate.error instanceof ApiError || saveTemplate.error instanceof Error
                ? saveTemplate.error.message
                : 'Could not save that template.'}
            </p>
          )}
        </div>

        {/* ---------------------------------------------------------------- catalogue */}

        {Boolean(products.data?.products.length) && (
          <div className="cad-field">
            <label>Sell from the catalogue</label>
            <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)', marginTop: 0 }}>
              One payment link is generated for the whole send — every recipient gets the same
              product and price. Replaces the message body and photo above.
            </p>
            <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
              {products.data?.products
                .filter((p) => p.active)
                .map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="cad-btn"
                    data-variant="quiet"
                    disabled={useProduct.isPending}
                    onClick={() => useProduct.mutate(p)}
                  >
                    {p.name} · {(p.priceMinor / 100).toFixed(2)} {p.currency}
                  </button>
                ))}
            </div>
            {useProduct.error && (
              <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                {useProduct.error instanceof ApiError || useProduct.error instanceof Error
                  ? useProduct.error.message
                  : 'Could not generate a payment link.'}
              </p>
            )}
          </div>
        )}

        {/* ---------------------------------------------------------------- what it costs */}

        {p && (
          <div className="stack gap-3">
            <div className="cad-stats">
              <Tile label="Recipients" value={String(p.recipients)} />
              <Tile label="Going today" value={String(p.scheduled)} />
              <Tile
                label="Deferred"
                value={String(p.deferred)}
                tone={p.deferred > 0 ? 'bad' : undefined}
              />
              <Tile label="Takes about" value={`${p.estimatedMinutes}m`} />
            </div>

            {/* The one figure that has to appear before credits are committed. */}
            <div className="stack gap-1">
              <div className="row between wrap gap-2" style={{ alignItems: 'baseline' }}>
                <span className="cad-eyebrow">On WhatsApp</span>
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  disabled={reachability.isPending || p.reachability.unknown === 0}
                  onClick={() => reachability.mutate({ auto: false })}
                >
                  {reachability.isPending
                    ? 'Checking…'
                    : p.reachability.unknown === 0
                      ? 'All checked'
                      : `Check ${p.reachability.unknown}`}
                </button>
              </div>
              <p
                className="cad-note"
                style={p.unreachable > 0 ? { borderColor: 'var(--cad-warn)' } : undefined}
              >
                {p.reachability.sentence}
                {p.unreachable > 0 &&
                  ` ${p.unreachable} of them will be skipped, so they cost nothing.`}
              </p>
              {unknownCount > AUTO_CHECK_MAX_UNKNOWN && (
                <p className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>
                  Checked automatically up to {AUTO_CHECK_MAX_UNKNOWN} unchecked numbers. This
                  audience is larger, so the check waits for you to press the button.
                </p>
              )}
              {reachabilityError && (
                <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{reachabilityError}</p>
              )}
            </div>

            {/* What happens to this send if a number goes away part-way through it. Shown
                above the per-number meters because it is about the fleet rather than about
                any one number's ceiling, and because it is the only line here that a customer
                can still act on — by connecting a spare — before pressing send. */}
            {p.fleet.warning && (
              <p className="cad-note" style={{ borderColor: 'var(--cad-warn)' }}>
                {p.fleet.warning}
              </p>
            )}

            {p.perNumber.map((n) => (
              <div key={n.numberId} className="stack gap-1">
                <div className="cad-meter-head">
                  <span>
                    {n.label} · <span className="cad-muted">{n.maturity}</span>
                  </span>
                  <b>
                    {n.count} / {n.cap}
                  </b>
                </div>
                <div className="cad-meter">
                  <span
                    style={{ width: `${n.cap > 0 ? Math.min(100, (n.count / n.cap) * 100) : 0}%` }}
                    data-tone={n.count >= n.cap ? 'warn' : 'good'}
                  />
                </div>
                {/* The warm-up reason explains why the ceiling is what it is, which is only
                    worth saying when the ceiling is not the thing being complained about. */}
                {(n.warning ?? n.warmup?.reason) && (
                  <p className="cad-note">{n.warning ?? n.warmup?.reason}</p>
                )}
              </div>
            ))}

            {audienceMode === 'paste' && recipients.length > 0 ? (
              <div className="stack gap-1">
                <div className="row between wrap gap-2" style={{ alignItems: 'baseline' }}>
                  <span className="cad-eyebrow">
                    Recipient {Math.min(previewIndex, recipients.length - 1) + 1} of {recipients.length} receives
                  </span>
                  <div className="row gap-2">
                    <button
                      type="button"
                      className="cad-btn"
                      data-variant="text"
                      disabled={previewIndex === 0}
                      onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="cad-btn"
                      data-variant="text"
                      disabled={previewIndex >= recipients.length - 1}
                      onClick={() => setPreviewIndex((i) => Math.min(recipients.length - 1, i + 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>
                <div className="cad-bubble">
                  {media && <div className="cad-muted">📎 {media.fileName}</div>}
                  {renderMessage(
                    body,
                    recipients[Math.min(previewIndex, recipients.length - 1)]?.variables ?? {},
                    variables,
                  )}
                </div>
              </div>
            ) : (
              p.sample && (
                <div className="stack gap-1">
                  <span className="cad-eyebrow">First recipient receives</span>
                  <div className="cad-bubble">
                    {media && <div className="cad-muted">📎 {media.fileName}</div>}
                    {p.sample}
                  </div>
                </div>
              )
            )}

            {p.missingVariableCount > 0 && (
              <div className="stack gap-1">
                <span className="cad-note" style={{ borderColor: 'var(--cad-warn)' }}>
                  {p.missingVariableCount} value{p.missingVariableCount === 1 ? ' is' : 's are'} missing, with
                  no fallback set — that recipient gets a blank in place of the variable.
                </span>
                <div className="cad-grid-frame">
                  <table className="cad-table">
                    <thead>
                      <tr>
                        <th>Recipient</th>
                        <th>Missing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.missingVariables.map((gap, i) => (
                        <tr key={`${gap.recipientId}-${gap.variable}-${i}`}>
                          <td>{gap.recipientId}</td>
                          <td>{`{${gap.variable}}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {p.missingVariableCount > p.missingVariables.length && (
                  <p className="cad-note">and {p.missingVariableCount - p.missingVariables.length} more.</p>
                )}
              </div>
            )}

            {p.blockers.length > 0 && (
              <div className="stack gap-2">
                {p.blockers.map((blocker) => (
                  <p key={blocker} className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                    {blocker}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {message && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{message}</p>
        )}

        <div className="row gap-2 wrap">
          <button
            type="button"
            className="cad-btn"
            disabled={
              (!body.trim() && !media) ||
              !audienceReady ||
              numbers.length === 0 ||
              selectedNumberIds.size === 0 ||
              create.isPending ||
              upload.isPending ||
              (p?.blockers.length ?? 0) > 0
            }
            onClick={() => {
              /**
               * The only action here that reaches people outside the workspace, and the only one
               * that cannot be taken back at all: messages already delivered stay delivered.
               * Everything else on this screen confirms; this did not.
               */
              const count = p?.scheduled ?? (audienceMode === 'segment' ? (p?.recipients ?? 0) : recipients.length)
              const question = scheduledFor
                ? `Schedule this send to ${count} recipient${count === 1 ? '' : 's'}?`
                : `Send to ${count} recipient${count === 1 ? '' : 's'} now?`
              if (confirm(`${question}\n\nMessages already delivered cannot be recalled, and bulk sending affects how WhatsApp rates this number.`)) create.mutate()
            }}
          >
            {create.isPending
              ? 'Starting…'
              : scheduledFor
                ? 'Schedule send'
                : `Send to ${p?.scheduled ?? (audienceMode === 'segment' ? (p?.recipients ?? 0) : recipients.length)}`}
          </button>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  )
}

/**
 * A starter CSV matching exactly the columns this composer already knows how to read back —
 * `phone`, `name`, and one column per `{variable}` token found in the message so far. Filling
 * it in and re-importing is the same "paste or import" path any other list goes through, not a
 * separate upload flow to keep in sync with it.
 */
function downloadRecipientTemplate(variableNames: string[]): void {
  const headers = ['phone', 'name', ...variableNames]
  const sampleRow = ['+14155551234', 'Priya', ...variableNames.map(() => '')]
  const csv = [headers, sampleRow].map((row) => row.join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'recipients-template.csv'
  link.click()
  URL.revokeObjectURL(url)
}

/** Names a column by its header where there is one, and by position where there is not. */
function columnLabel(rows: string[][], index: number, hasHeader: boolean): string {
  const header = hasHeader ? rows[0]?.[index]?.trim() : ''
  if (header) return header
  const sample = (hasHeader ? rows[1] : rows[0])?.[index]?.trim()
  return sample ? `Column ${index + 1} — ${sample.slice(0, 24)}` : `Column ${index + 1}`
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <div className="cad-stat">
      <span className="cad-stat-label">{label}</span>
      <span className="cad-stat-value" data-tone={tone}>
        {value}
      </span>
    </div>
  )
}
